export type UploadType = 'image' | 'txt' | 'file';
export type UrlType = 'video_url' | 'portfolio_url' | 'teachable_url' | 'google_drive_url' | 'stemi_lab_assistant_url' | 'chatbot_url' | 'bit_console_code_url';
export type ChallengeCategory = 'ai' | 'robotics' | 'gen_future' | 'bit_console' | 'vidi_x';

export interface UploadField {
  type: UploadType;
  slug: string;
  acceptedFormats?: string[]; // passed to Cloudinary clientAllowedFormats; for 'image' type handled via isImageOnly
  label?: string;
  group?: string; // polja s istim groupom = bar jedno mora biti ispunjeno
}

export interface UrlField {
  type: UrlType;
  fieldKey: string;
  label?: string;
  group?: string;
  optional?: boolean;
}

export interface CheckboxField {
  type: 'checkbox';
  fieldKey: string;
  label?: string;
  group?: string;
}

// validacija URL formata po tipu — ako pattern ne odgovara, prikazuje se hint korisniku
export const URL_PATTERNS: Partial<Record<UrlType, { pattern: RegExp; hint: string }>> = {
  google_drive_url: {
    pattern: /drive\.google\.com\/.+/,
    hint: 'poveznica na Google Drive datoteku ili mapu (drive.google.com/...)',
  },
  teachable_url: {
    pattern: /teachablemachine\.withgoogle\.com\/.+/,
    hint: 'poveznica na Teachable Machine model (teachablemachine.withgoogle.com/...)',
  },
  stemi_lab_assistant_url: {
    pattern: /lab\.stemi\.education\/assistants\/.+/,
    hint: 'poveznica na STEMI Lab asistenta (lab.stemi.education/assistants/...)',
  },
  bit_console_code_url: {
    pattern: /code\.circuitmess\.com\/.+/,
    hint: 'poveznica na Bit konzolu kod (code.circuitmess.com/...)',
  },
  chatbot_url: {
    pattern: /chatbot\.stemi\.education\/.+/,
    hint: 'poveznica na STEMI Chatbot (chatbot.stemi.education/...)',
  },
};

export type SubmissionField = UploadField | UrlField | CheckboxField

export interface ChallengeConfig {
  name: string;
  category: ChallengeCategory;
  submissions: SubmissionField[];
}

// Izazovi program id: b6798657-58ee-4b5d-bb84-171645e14a23
export const CHALLENGE_CONFIGS: Record<string, ChallengeConfig> = {
  ai_pocetni_logo_3: {
    name: 'Izrada AI logotipa',
    category: 'ai',
    submissions: [{ type: 'image', slug: 'ai_pocetni_logo_3' }],
  },
  ai_pocetni_slika_3: {
    name: 'Izrada AI slike',
    category: 'ai',
    submissions: [{ type: 'image', slug: 'ai_pocetni_slika_3' }],
  },
  ai_pocetni_pjesma_3: {
    name: 'Izrada AI pjesme',
    category: 'ai',
    submissions: [
      { type: 'txt', slug: 'ai_pocetni_pjesma_3' },
      { type: 'image', slug: 'ai_pocetni_pjesma_3_cover' }
    ],
  },
  ai_znalci_model_strojnog_ucenja: {
    name: 'Izrada modela strojnog učenja',
    category: 'ai',
    submissions: [
      { type: 'image', slug: 'ai_znalci_model_strojnog_ucenja_cover' },
      { type: 'teachable_url', fieldKey: 'ai_znalci_model_strojnog_ucenja_teachable_url' },
      { type: 'google_drive_url', fieldKey: 'ai_znalci_model_strojnog_ucenja_google_drive_url' },
      { type: 'checkbox', fieldKey: 'ai_znalci_model_strojnog_ucenja_gdrive_is_public', label: 'Je li Google Drive poveznica javno dostupna?' },
    ],
  },
  ai_napredni_game_dev_3: {
    name: 'Razvoj igre s računalnim vidom',
    category: 'ai',
    submissions: [
      { type: 'image', slug: 'ai_napredni_game_dev_3_cover' },
      { type: 'file', slug: 'ai_napredni_game_dev_3', acceptedFormats: ['sb3'], label: 'Ovdje učitajte .sb3 datoteku.' },
      { type: 'teachable_url', fieldKey: 'ai_napredni_game_dev_3_teachable_url' },
      { type: 'google_drive_url', fieldKey: 'ai_napredni_game_dev_3_google_drive_url' },
      { type: 'checkbox', fieldKey: 'ai_napredni_game_dev_3_gdrive_is_public', label: 'Je li Google Drive poveznica javno dostupna?' },
    ],
  },
  ai_znalci_funkcionalni_chatbot: {
    name: 'Razvoj funkcionalnog chatbota',
    category: 'ai',
    submissions: [
      { type: 'image', slug: 'ai_znalci_funkcionalni_chatbot_cover' },
      { type: 'stemi_lab_assistant_url', fieldKey: 'ai_znalci_funkcionalni_chatbot_stemi_lab_assistant_url' },
    ],
  },
  ai_genijalci_chatbot_udruga: {
    name: 'Razvoj chatbota za školu ili udrugu',
    category: 'ai',
    submissions: [
      { type: 'image', slug: 'ai_genijalci_chatbot_udruga_cover' },
      { type: 'chatbot_url', fieldKey: 'ai_genijalci_chatbot_udruga_chatbot_url', label: 'Link na chatbota' },
      { type: 'checkbox', fieldKey: 'ai_genijalci_chatbot_udruga_editor_finished_confirmation', label: 'Potvrđujem da sam ispounio/la sve informacije o chatbotu u 1. I 2. koraku' }
    ],
  },
  ras_srednji_hexapod: {
    name: '3D modeliranje Hexapod elementa',
    category: 'robotics',
    submissions: [
      { type: 'image', slug: 'ras_srednji_hexapod_cover', label: 'Ovdje učitajte naslovnu sliku vašeg projekta.' },
      { type: 'file', slug: 'ras_srednji_krovna', acceptedFormats: ['step'], label: 'Ovdje učitajte vašu .step datoteku za krovnu ploču.' },
      { type: 'file', slug: 'ras_srednji_temeljna', acceptedFormats: ['step'], label: 'Ovdje učitajte vašu .step datoteku za temeljnu ploču.' },
    ],
  },
  ras_napredni_hexapod_ples: {
    name: 'Dizajniranje i programiranje plesnog nastupa Hexapod robota',
    category: 'robotics',
    submissions: [
      { type: 'image', slug: 'ras_napredni_hexapod_ples_cover', label: 'Ovdje učitajte naslovnu sliku vašeg projekta.' },
      { type: 'google_drive_url', fieldKey: 'ras_napredni_hexapod_ples_google_drive_url', label: 'Poveznica na Google Drive s videozapisom plesa.' },
      { type: 'checkbox', fieldKey: 'ras_napredni_hexapod_ples_gdrive_is_public', label: 'Je li Google Drive poveznica javno dostupna?' },
    ],
  },
  ras_napredni_hexapod_redizajn: {
    name: 'Redizajn Hexapod robota',
    category: 'robotics',
    submissions: [
      { type: 'image', slug: 'ras_napredni_hexapod_redizajn_cover', label: 'Ovdje učitajte naslovnu sliku vašeg projekta.' },
      { type: 'file', slug: 'ras_napredni_step_cijeli', acceptedFormats: ['step'], label: 'Ovdje učitajte vašu .step datoteku.' },
    ],
  },
  ras_napredni_hexapod_robotska_ruka: {
    name: 'Dizajniranje i izrada robotske ruke za Stemi Hexapod robot',
    category: 'robotics',
    submissions: [
      { type: 'image', slug: 'ras_napredni_hexapod_robotska_ruka_cover', label: 'Ovdje učitajte naslovnu sliku vašeg projekta.' },
      { type: 'file', slug: 'ras_napredni_izazov_step_datoteka_1', acceptedFormats: ['step'], label: 'Učitajte vašu .step datoteku.' },
      { type: 'file', slug: 'ras_napredni_izazov_aia_datoteka', acceptedFormats: ['aia'], label: 'Učitajte vašu .aia datoteku.' },
      { type: 'google_drive_url', fieldKey: 'ras_napredni_hexapod_robotska_ruka_google_drive_url', label: 'Poveznica na Google Drive s videozapisom rada.' },
      { type: 'checkbox', fieldKey: 'ras_napredni_hexapod_robotska_ruka_gdrive_is_public', label: 'Je li Google Drive poveznica javno dostupna?' },
    ],
  },
  gen_future_napredni_4: {
    name: 'Izrada multimedijskog portfelja',
    category: 'gen_future',
    submissions: [
      { type: 'image', slug: 'gen_future_napredni_4_cover', label: 'Ovdje učitajte naslovnu sliku vašeg projekta.' },
      { type: 'file', slug: 'gen_future_napredni_4', label: 'Ovdje učitajte datoteku s projektom.', group: 'portfolio' },
      { type: 'portfolio_url', fieldKey: 'gen_future_napredni_4_url', label: 'Poveznica na projekt (prezentacija, PDF, video...)', group: 'portfolio' },
    ],
  },
  gen_future_napredni_dobrobit: {
    name: 'Izrada dobro:bit portfelja',
    category: 'gen_future',
    submissions: [
      { type: 'image', slug: 'gen_future_napredni_dobrobit_cover', label: 'Ovdje učitajte naslovnu sliku vašeg projekta.' },
      { type: 'file', slug: 'gen_future_napredni_dobrobit_file', label: 'Ovdje učitajte datoteku s projektom.', group: 'portfolio' },
      { type: 'portfolio_url', fieldKey: 'gen_future_napredni_dobrobit_url', label: 'Poveznica na projekt (prezentacija, PDF, video...)', group: 'portfolio' },
    ],
  },
  gen_future_napredni_samsung: {
    name: 'Samsung challenge',
    category: 'gen_future',
    submissions: [
      { type: 'image', slug: 'gen_future_napredni_samsung_cover', label: 'Ovdje učitajte naslovnu sliku vašeg projekta.' },
      { type: 'file', slug: 'gen_future_napredni_samsung_file', label: 'Ovdje učitajte datoteku s projektom.', group: 'portfolio' },
      { type: 'portfolio_url', fieldKey: 'gen_future_napredni_samsung_url', label: 'Poveznica na projekt (prezentacija, PDF, video...)', group: 'portfolio' },
    ],
  },
  game_dev_napredni_4: {
    name: 'Izrada videoigre za Bit konzolu',
    category: 'bit_console',
    submissions: [
      { type: 'image', slug: 'game_dev_napredni_4_cover', label: 'Ovdje učitajte naslovnu sliku vašeg projekta.' },
      { type: 'file', slug: 'game_dev_napredni_4', acceptedFormats: ['txt'], label: 'Ovdje učitajte vaš kod kao .txt datoteku.', group: 'code' },
      { type: 'bit_console_code_url', fieldKey: 'game_dev_napredni_4_bit_console_code_url', label: 'Ili navedite poveznicu na vaš kod na Bit konzoli.', group: 'code' },
      { type: 'google_drive_url', fieldKey: 'game_dev_napredni_4_google_drive_url', label: 'Poveznica na Google Drive s videozapisom igre.' },
      { type: 'checkbox', fieldKey: 'game_dev_napredni_4_gdrive_is_public', label: 'Je li Google Drive poveznica javno dostupna?' },
    ],
  },
  vidix_pdf: {
    name: 'Izrada VIDI X projekta',
    category: 'vidi_x',
    submissions: [
      { type: 'image', slug: 'vidix_pdf_cover', label: 'Ovdje učitajte naslovnu sliku vašeg projekta.' },
      { type: 'file', slug: 'vidix_kod', acceptedFormats: ['ino', 'txt'], label: 'Ovdje učitajte vaš kod kao .ino ili .txt datoteku.' },
      { type: 'file', slug: 'vidix_pdf', acceptedFormats: ['pdf'], label: 'Ovdje učitajte prezentaciju kao PDF datoteku.' },
      { type: 'google_drive_url', fieldKey: 'vidix_pdf_google_drive_url', label: 'Poveznica na Google Drive s videozapisom projekta.' },
      { type: 'checkbox', fieldKey: 'vidix_pdf_gdrive_is_public', label: 'Je li Google Drive poveznica javno dostupna?' },
    ]
  }
};
